/**
 * AdminOrgDetailPage — deep-dive on a single org for platform admins.
 * Shows users + roles, locations, enabled modules, recent uploads, activity totals.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, ArrowLeft, Building2, Users, MapPin, FileText,
  ShoppingBag, Receipt, AlertCircle, Layers,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOrgDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { isPlatformAdmin, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !isPlatformAdmin) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/admin/orgs/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load org'))
      .finally(() => setLoading(false));
  }, [token, id, isPlatformAdmin]);

  if (adminLoading) return null;
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Access denied</h2>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground">Loading org…</div>;
  }
  if (error || !data) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/admin/orgs')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Card className="p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm">{error || 'Org not found'}</p>
        </Card>
      </div>
    );
  }

  const { organization, users, locations, enabled_modules, recent_uploads, stats } = data;

  return (
    <div className="space-y-6" data-testid="admin-org-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/orgs')} data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> All orgs
        </Button>
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Shield className="w-3.5 h-3.5" /> Platform Admin
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
          <Building2 className="w-7 h-7 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold">{organization.name}</h1>
          {organization.description && (
            <p className="text-sm text-muted-foreground mt-1">{organization.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Created {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : '—'}
            {' · '}ID: <code className="text-[10px] bg-muted px-1 rounded">{organization.id}</code>
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Users" value={stats.users} />
        <StatCard icon={MapPin} label="Locations" value={stats.locations} />
        <StatCard icon={FileText} label="Uploads" value={stats.total_uploads} />
        <StatCard icon={ShoppingBag} label="Orders" value={stats.total_orders} />
        <StatCard icon={Receipt} label="Expenses" value={stats.total_expenses} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Users */}
        <Card data-testid="admin-org-users">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {users.map((u) => (
                <div key={u.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize">{u.role || 'member'}</Badge>
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">No users</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Locations */}
        <Card data-testid="admin-org-locations">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Locations ({locations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {locations.map((l) => (
                <div key={l.id} className="px-4 py-3 text-sm">
                  <div className="font-medium">{l.name}</div>
                  {l.address && <div className="text-xs text-muted-foreground">{l.address}</div>}
                </div>
              ))}
              {locations.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">No locations</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Enabled modules */}
        <Card data-testid="admin-org-modules">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="w-4 h-4" /> Enabled modules ({enabled_modules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {enabled_modules.map((m) => (
                <Badge key={m} variant="secondary" className="capitalize">{m}</Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Phase 2 of the Platform Admin Console will let admins toggle these directly without impersonation.
            </p>
          </CardContent>
        </Card>

        {/* Recent uploads */}
        <Card data-testid="admin-org-uploads">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Recent uploads ({recent_uploads.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-80 overflow-y-auto">
              {recent_uploads.map((u) => (
                <div key={u.id} className="px-4 py-2.5 text-xs">
                  <div className="font-medium text-sm truncate">{u.filename}</div>
                  <div className="text-muted-foreground flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{u.upload_type || u.file_type}</Badge>
                    <span>{u.created_at ? new Date(u.created_at).toLocaleString() : ''}</span>
                  </div>
                </div>
              ))}
              {recent_uploads.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">No uploads yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-semibold">{value?.toLocaleString() ?? 0}</div>
    </CardContent>
  </Card>
);
